"use client";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGamePageAnalytics } from "@/context/AnalyticsProvider/gamePage";
import { cn } from "@/lib/utils";
import { PlayButton } from "@components/NewGamePage/TrailerPlayer/PlayButton";
import { Video as VideoIcon } from "lucide-react";
import dynamic from "next/dynamic";
import { useCallback, useState } from "react";
import { VideoModal } from "./VideoModal";

// Dynamically import ReactPlayer with SSR disabled to prevent hydration mismatch
const ReactPlayer = dynamic(() => import("react-player"), { ssr: false });

export interface VideoCardProps {
  videoUrl?: string;
  thumbnailUrl?: string;
  title?: string;
  gameTitle?: string;
  gameIcon?: string;
  className?: string;
  gameId?: string;
}

export function TrailerPlayer({
  videoUrl,
  thumbnailUrl,
  gameTitle,
  gameIcon,
  className,
  gameId,
}: VideoCardProps) {
  const { track } = useGamePageAnalytics();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenChange = useCallback(() => {
    setIsModalOpen((prev) => !prev);
    if (gameId) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "play-trailer",
          control: isModalOpen ? "ModalClose" : "ModalOpen",
          controlType: ControlType.BUTTON,
          gameId,
          gameName: gameTitle,
          version: "v3",
        },
      });
    }
  }, [gameId, gameTitle, track, isModalOpen]);

  return (
    <>
      <div
        // biome-ignore lint/a11y/useSemanticElements: use div instead of button as we have child elements not supported by <button>
        role="button"
        tabIndex={0}
        onClick={handleOpenChange}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && handleOpenChange()
        }
        aria-label="Play video"
        className={cn(
          "relative aspect-video glass-container-border group cursor-pointer",
          className,
        )}
      >
        {/* Background: video when in view, else image fallback */}
        <div className="absolute inset-0 overflow-hidden rounded-[1.25rem]">
          {videoUrl ? (
            <ReactPlayer
              url={videoUrl}
              playing={!isModalOpen}
              loop
              muted
              volume={0}
              controls={false}
              playsinline
              width="100%"
              height="100%"
              config={{ file: { attributes: { preload: "metadata" } } }}
            />
          ) : thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt="Video thumbnail"
              className="w-full h-full object-cover object-center select-none"
              draggable={false}
            />
          ) : (
            <div className="w-full h-full bg-background/50" />
          )}
        </div>

        {/* Dim overlay */}
        <div className="absolute inset-0 size-full rounded-[1.25rem] pointer-events-none bg-darken-overlay" />

        {/* Title */}
        <div className="absolute left-[5%] top-[10%] text-neutral-50 font-suisse flex items-center gap-x-1.5 @4xl:flex-col @4xl:items-start">
          <VideoIcon className="@md:size-6 size-5" />
          <p className="@md:text-lg text-sm font-semibold">Official Trailer</p>
        </div>

        <PlayButton />
      </div>

      <VideoModal
        isModalOpen={isModalOpen}
        handleOpenChange={handleOpenChange}
        videoUrl={videoUrl}
        gameIcon={gameIcon}
        gameTitle={gameTitle}
        gameId={gameId}
      />
    </>
  );
}
